/**
 * TransactionImportQueue.tsx — 交易导入待归属队列（ADM-FIN-S009，财务页「交易归属」tab）
 *
 * 接真：GET /api/v1/finance/transaction-imports（useTransactionImportQueue，offset 分页 + App scope 注入 app_id）。
 * 队列 = Adapty S3 交易导入中无法自动归属的行（默认 attribution_status=PERMANENT_UNATTRIBUTED，DLQ 交接口径）；
 * 运营线下核验后经 AttributionDialog 人工归属（写 owner_user_id + 下轮 reconcile 重放投影）。
 * 金额口径 raw-local（base_amount_local 原币本币额，非 minor units；金融解析单源 C 端，dev-docs 60-订阅 §8）。
 * 已投影行（entitlement PROJECTED / 任一 effect EXECUTED）改绑会被后端拒（CONFLICT），列表以 chip 提示。
 *
 * 两种过滤分工：左侧搜索框是**页内客户端筛选**（交易号 / 产品 / 用户 ID 子串，只作用于已加载页）；
 * 「原始交易号」是**服务端等值过滤**（A1 §10.0 的 `original_transaction_id`）—— 台账侧
 * `permanent_ambiguous_lineage` 行没有 `transaction_id`，运营只能以 lineage 跨页定位队列行，
 * 再取行内 `(app_id, transaction_id)` 走归属写端点。子串筛选跨不了页，替代不了它。
 */

import type { TransactionImportAttributionStatus } from '@novel-admin/shared';
import { useEffect, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { type TxnImportQueueRow, useTransactionImportQueue } from '~/data/queries';
import { currencySymbol, fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { ContextMenu, type ContextMenuItem } from '~/ui/ContextMenu';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { Select } from '~/ui/Select';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { AttributionDialog } from './dialogs/AttributionDialog';

// 归属状态闭集 → 中文标签（dev-docs 60-订阅 §2.1）
const ATTR_STATUS_ZH: Record<TransactionImportAttributionStatus, string> = {
  PENDING_ATTRIBUTION: '待归属',
  ATTRIBUTED: '已自动归属',
  PERMANENT_UNATTRIBUTED: '永久待归属',
  MANUALLY_ATTRIBUTED: '已人工归属',
};
// 可人工归属的状态（未终结归属 → 可操作；已归属行由后端幂等 / CONFLICT 兜底，不给行内入口）
const ATTRIBUTABLE = new Set<TransactionImportAttributionStatus>([
  'PENDING_ATTRIBUTION',
  'PERMANENT_UNATTRIBUTED',
]);

// 是否已投影（任一效果已执行）：已投影行改绑被后端拒（CONFLICT）
function isProjected(r: TxnImportQueueRow): boolean {
  return (
    r.entitlement_status === 'PROJECTED' ||
    r.credit_effect_status === 'EXECUTED' ||
    r.refund_effect_status === 'EXECUTED' ||
    r.finance_purchase_status === 'EXECUTED' ||
    r.finance_refund_status === 'EXECUTED'
  );
}

export function TransactionImportQueue() {
  const appId = useAppScope((s) => s.appId);
  const pageSize = useAppScope((s) => s.tablePageSize);
  // 归属是 operator+ 写操作（后端 requireRole）；前端按角色隐藏入口（仅 UX，后端权威），对齐 GrantBonusForm
  const role = useAuthStore((s) => s.user?.role);
  const canAttribute = role === 'root' || role === 'admin' || role === 'operator';
  const [status, setStatus] =
    useState<TransactionImportAttributionStatus>('PERMANENT_UNATTRIBUTED');
  const [page, setPage] = useState(1);
  // lineage 服务端过滤：输入态与提交态分开 —— 等值匹配要整串 ID，逐字符打服务端既无意义又费额度
  const [lineageInput, setLineageInput] = useState('');
  const [lineage, setLineage] = useState('');
  const {
    data: rows,
    total,
    isLoading,
    isError,
  } = useTransactionImportQueue({
    attributionStatus: status,
    originalTransactionId: lineage || undefined,
    page,
    limit: pageSize,
  });

  // App scope / 状态 / 页档变化 → 回第一页
  // biome-ignore lint/correctness/useExhaustiveDependencies: 专门在 appId/status/pageSize/lineage 变化时重置页码
  useEffect(() => {
    setPage(1);
  }, [appId, status, pageSize, lineage]);
  // 越界页 clamp：切 scope / total 收缩后停旧页码 → 后端返空表，加载完成后 clamp 回末页重取
  useEffect(() => {
    if (isLoading) return;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [total, pageSize, page, isLoading]);

  const [q, setQ] = useState('');
  const [target, setTarget] = useState<TxnImportQueueRow | null>(null);

  // 客户端筛选 over 已加载页（交易号 / 产品 / 归属线索 user id）
  const shown = rows.filter((r) => {
    if (!q) return true;
    const k = q.toLowerCase();
    return (
      r.transaction_id.toLowerCase().includes(k) ||
      r.vendor_product_id.toLowerCase().includes(k) ||
      (r.customer_user_id ?? '').toLowerCase().includes(k)
    );
  });

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8 }}>
        <SearchInput
          width={280}
          placeholder="交易号 / 产品 ID / 用户 ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as TransactionImportAttributionStatus)}
          style={{ width: 150 }}
        >
          {(Object.keys(ATTR_STATUS_ZH) as TransactionImportAttributionStatus[]).map((s) => (
            <option key={s} value={s}>
              {ATTR_STATUS_ZH[s]}
            </option>
          ))}
        </Select>
        <SearchInput
          width={260}
          placeholder="原始交易号（服务端精确匹配）"
          value={lineageInput}
          onChange={(e) => setLineageInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setLineage(lineageInput.trim());
          }}
          onClear={() => {
            setLineageInput('');
            setLineage('');
          }}
        />
        {lineage && (
          <button
            type="button"
            className="chip chip-info"
            title="清除 lineage 过滤"
            style={{ fontSize: 10.5, cursor: 'pointer' }}
            onClick={() => {
              setLineageInput('');
              setLineage('');
            }}
          >
            <span className="mono">lineage={lineage}</span>
            <Icons.IconX size={10} style={{ marginLeft: 4 }} />
          </button>
        )}
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          共 {total} 条（本页筛 {shown.length}）
        </span>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <Table className="tbl">
          <thead>
            <tr>
              <th>交易号</th>
              <th>App</th>
              <th>产品</th>
              <th className="num">金额</th>
              <th>退款</th>
              <th>投影</th>
              <th>状态</th>
              <th>时间</th>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const projected = isProjected(r);
              const items: ContextMenuItem[] = [
                ...(canAttribute && ATTRIBUTABLE.has(r.attribution_status)
                  ? [
                      {
                        label: '归属到用户',
                        icon: 'IconLink',
                        onClick: () => setTarget(r),
                      } as const,
                    ]
                  : []),
                {
                  label: '复制交易号',
                  icon: 'IconCopy',
                  onClick: () => {
                    navigator.clipboard?.writeText(r.transaction_id);
                    toast(`已复制 ${r.transaction_id}`, { tone: 'pos', icon: 'IconCopy' });
                  },
                },
              ];
              return (
                <tr key={`${r.app_id}:${r.transaction_id}`}>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.transaction_id}
                  </td>
                  <td>
                    <span className="chip" style={{ fontSize: 10 }}>
                      {r.appName}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {r.vendor_product_id}
                  </td>
                  <td className="num mono">
                    {/* 币种未知不臆造符号（base_currency 可独立于金额为 null），与 AttributionDialog 一致 */}
                    {r.base_amount_local
                      ? `${r.base_currency ? currencySymbol(r.base_currency) : ''}${r.base_amount_local}`
                      : '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.refund_raw_count > 0 ? (
                      <span title="退款投影候选数 / 原始退款事件数（含疑似重发）">
                        {r.refund_candidate_count}/{r.refund_raw_count}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`chip ${projected ? 'chip-warn' : ''}`}
                      style={{ fontSize: 10 }}
                      title={projected ? '效果已投影 · 改绑将被拒（CONFLICT）' : '尚未投影'}
                    >
                      {projected ? '已投影' : '待投影'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`chip ${
                        r.attribution_status === 'MANUALLY_ATTRIBUTED' ||
                        r.attribution_status === 'ATTRIBUTED'
                          ? 'chip-pos'
                          : r.attribution_status === 'PERMANENT_UNATTRIBUTED'
                            ? 'chip-neg'
                            : 'chip-warn'
                      }`}
                      style={{ fontSize: 10 }}
                    >
                      {ATTR_STATUS_ZH[r.attribution_status]}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                    {fmtDateTime(r.occurred_at)}
                  </td>
                  <td>
                    <ContextMenu items={items}>
                      <button className="btn btn-ghost btn-icon" style={{ width: 22, height: 22 }}>
                        <Icons.IconMoreV size={12} />
                      </button>
                    </ContextMenu>
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && !isLoading && (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    textAlign: 'center',
                    padding: 32,
                    // 错误态与空态区分：接口失败用 neg 色，避免把「加载失败」误读成「无待归属交易」
                    color: isError ? 'var(--neg)' : 'var(--fg-muted)',
                    fontSize: 12,
                  }}
                >
                  {isError ? '队列加载失败，请刷新重试' : q ? '无匹配的交易' : '暂无待归属交易'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {/* offset 分页：真实 total → 显示总数 / 总页数 + 跳任意页 */}
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={setPage}
          totalMode="exact"
        />
      </div>

      {target && <AttributionDialog row={target} onClose={() => setTarget(null)} />}
    </div>
  );
}
